import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Moon, Sun, Globe, Shield, Save, Link as LinkIcon, SpellCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Configuracoes() {
  const { user, checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(() => {
    const saved = localStorage.getItem('spellCheckEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [formData, setFormData] = useState({
    website: '',
    instagram: '',
    linkedin: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        website: user.website || '',
        instagram: user.instagram || '',
        linkedin: user.linkedin || ''
      });
    }
  }, [user]);

  const handleSpellCheckToggle = () => {
    const newValue = !spellCheckEnabled;
    setSpellCheckEnabled(newValue);
    localStorage.setItem('spellCheckEnabled', JSON.stringify(newValue));
    toast.success(newValue ? 'Correção ortográfica ativada' : 'Correção ortográfica desativada');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        formData,
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="configuracoes-page">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-light mb-2">Configurações</h1>
            <p className="text-muted-foreground">Personalize sua experiência</p>
          </div>

          {/* Appearance */}
          <div className="bg-card border border-border/40 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              {theme === 'dark' ? <Moon className="w-5 h-5 text-amber-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
              <h3 className="text-xl font-serif">Aparência</h3>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Tema</p>
                <p className="text-sm text-muted-foreground">
                  {theme === 'dark' ? 'Modo escuro ativado' : 'Modo claro ativado'}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={toggleTheme}
                className="gap-2"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? (
                  <>
                    <Sun size={18} />
                    Modo Claro
                  </>
                ) : (
                  <>
                    <Moon size={18} />
                    Modo Escuro
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Spell Check */}
          <div className="bg-card border border-border/40 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <SpellCheck className="w-5 h-5 text-amber-500" />
              <h3 className="text-xl font-serif">Correção Ortográfica</h3>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Sugestões de correção</p>
                <p className="text-sm text-muted-foreground">
                  {spellCheckEnabled 
                    ? 'Sugerir correções para erros ortográficos' 
                    : 'Correções desativadas'}
                </p>
              </div>
              <Button
                variant={spellCheckEnabled ? 'default' : 'outline'}
                onClick={handleSpellCheckToggle}
                className={spellCheckEnabled ? 'bg-amber-500 hover:bg-amber-600 text-stone-900' : ''}
                data-testid="spell-check-toggle"
              >
                {spellCheckEnabled ? 'Ativada' : 'Desativada'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              O Author's Key nunca altera seu texto automaticamente. Quando ativada, apenas sugere correções que você pode aceitar ou ignorar.
            </p>
          </div>

          {/* Social Links */}
          <form onSubmit={handleSubmit}>
            <div className="bg-card border border-border/40 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-5 h-5 text-amber-500" />
                <h3 className="text-xl font-serif">Links e Redes Sociais</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <LinkIcon size={14} />
                    Site Pessoal
                  </label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://seu-site.com"
                    data-testid="website-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Instagram</label>
                  <Input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="@seu_usuario"
                    data-testid="instagram-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">LinkedIn</label>
                  <Input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/seu-perfil"
                    data-testid="linkedin-input"
                  />
                </div>
              </div>
            </div>

            {/* Account Info (Read-only) */}
            <div className="bg-card border border-border/40 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-5 h-5 text-amber-500" />
                <h3 className="text-xl font-serif">Conta</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome</label>
                  <Input
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Vinculado à sua conta Google</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Vinculado à sua conta Google</p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              data-testid="save-settings-button"
            >
              <Save className="mr-2" size={20} />
              {loading ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
